import { get, cloneDeep, isEqual } from 'lodash'
import { logoutUser } from '../users'
import type {
  IEntityMaskData,
  IEntityDataItem,
  ISelectItem,
  IDivider,
  TSortFunctionResult,
} from '~/types/models'
import { simplePluralize } from '~/cynqueModules/utils/pluralize'
import { isIServiceError } from '~/cynqueModules/handleAsyncDataErrors'

/**
 * Log the user out ONLY for a genuine auth failure.
 *
 * These list-builders used to call `logoutUser()` from their catch blocks
 * unconditionally, so ANY error while assembling a select's items — including a
 * plain TypeError over malformed data — ended the session. That actively hid
 * the cause: a data bug presented as "it crashed and logged me out", and the
 * 403s that followed looked like the real problem.
 *
 * 403/422 are what the API returns for an expired/invalid token (the same pair
 * handleAsyncDataErrors treats as session expiry).
 *
 * ⚠ Kept deliberately IN SYNC with the 403/422 branch in handleAsyncDataErrors,
 * which is this predicate's source of truth. Write-path 400/422 no longer reach
 * either one — they are typed as `ValidationError` and returned above this check
 * — but the read path still relies on 422-means-expired, so neither copy was
 * narrowed. Change one without the other and they drift.
 */
function isAuthFailure(e: unknown): boolean {
  return isIServiceError(e) && (e.statusCode === 403 || e.statusCode === 422)
}

//
// Interfaces
//

export interface IGetSelectList {
  calledFromFn: string
  inputFields: IEntityMaskData[]
  targetFieldName?: string
  getService: Function
  getServiceId?: string

  filterFunction?: IGetSelectListFilterFunction
  filterFieldName?: string
  disableFunction?: IGetSelectListDisableFunction
  disableFieldName?: string
  sortFunction?: IGetSelectListSortFunction
  labelPropertyName: string
  doNotRemapToJustIdAndLabel?: boolean
  getServiceQueryParameter?: any
  // Appended to the label of every item `disableFunction` disabled, e.g.
  // ' (already added)'. Applied after the sort so it can't perturb ordering.
  disabledItemLabelSuffix?: string
}
export interface IGetSelectListBasedOnField extends IGetSelectList {
  basedOnFieldName: string
}

export interface IGetSelectListBasedOnTwoFields
  extends IGetSelectListBasedOnField {
  basedOnField2Name: string
}

export interface IGetSelectListBasedOnParentIdAndFieldValueId
  extends IGetSelectListBasedOnField {
  parentId: string
}

export interface IGetSelectListBasedOnParentIdAndTwoFieldValueIds
  extends IGetSelectListBasedOnField {
  parentId: string
  basedOnField2Name: string
  noErrorOnFalseyBasedOnFieldName?: boolean
}

export interface IGetSelectListBasedOnParentIdAnAssociationIdAndAFieldValueId
  extends IGetSelectListBasedOnField {
  parentId: string
  associationId: string
}

export interface IGetSelectListFilterFunction {
  (filterField: IEntityMaskData | undefined):
    | IGetSelectListFilterFunctionFunction
    | Promise<IGetSelectListFilterFunctionFunction>
}
export interface IGetSelectListFilterFunctionFunction {
  (item: IEntityDataItem): boolean | Promise<boolean>
}

export interface IGetSelectListDisableFunction {
  (
    filterField: IEntityMaskData | undefined
  ): IGetSelectListDisableFunctionFunction
}
export interface IGetSelectListDisableFunctionFunction {
  (item: IEntityDataItem): boolean
}

export interface IGetSelectListSortFunction {
  (a: any, b: any): TSortFunctionResult // Tighter typing gets us into trouble cause
  // these are likely to be either ISelectItem or IEntityDataItem, which are
  // are not type compatible and can't be 'or'ed'
}

//
// Internal functions
//

interface IGetSelectListFromListData
  extends Omit<
    IGetSelectList,
    'calledFromFn' | 'targetFieldName' | 'getService' | 'getServiceId'
  > {
  listData: IEntityDataItem[]
}

/**
 * A whole record, titled the way a select list titles it: its `label` taken
 * from `labelPropertyName` (a lodash path), overriding any `label` it already
 * has. The `doNotRemapToJustIdAndLabel` lists are built with this, and a
 * doReturnObject field's VALUE can be too -- it comes from the entity data, not
 * the list, and the select titles a value with no matching item by its own
 * `label` (e.g. Edit Kit's TSD while its list is empty).
 */
export function withLabelFromProperty<T extends object>(
  o: T,
  labelPropertyName: string
): T & { label: any } {
  return { ...o, label: get(o, labelPropertyName) }
}

async function _getSelectListFromListData({
  listData,
  inputFields,
  filterFunction,
  sortFunction,
  disableFunction,
  filterFieldName,
  disableFieldName,
  labelPropertyName,
  doNotRemapToJustIdAndLabel,
  disabledItemLabelSuffix,
}: IGetSelectListFromListData): Promise<ISelectItem[]> {
  console.debug('Getting selectList using _getSelectListFromListData')

  // Set up filter

  const filterField: IEntityMaskData | undefined = filterFieldName
    ? inputFields.find((ifield) => ifield.name == filterFieldName)
    : undefined

  console.debug('Filtering on field', filterField)
  console.debug('Filter function...')
  console.debug(filterFunction)

  const filterFn = filterFunction
    ? await filterFunction(filterField)
    : (item: IEntityDataItem) => true

  // set up disable

  const disableField: IEntityMaskData | undefined = disableFieldName
    ? inputFields.find((ifield) => ifield.name == disableFieldName)
    : undefined

  console.debug('Disabling based on field', disableField)
  console.debug('Disable function...')
  console.debug(disableFunction)

  const disableFn = disableFunction
    ? disableFunction(disableField)
    : (item: IEntityDataItem) => false

  // set up map

  // `disabled` is stamped onto every item below, before the map runs. Both shapes
  // are emitted on purpose: Vuetify 2 reads the top-level `disabled` (its
  // item-disabled prop defaults to 'disabled'), while Vuetify 3 reads per-item
  // props only from `item.props` (its item-props prop defaults to 'props') and
  // ignores a top-level `disabled` entirely. Each version ignores the other's key,
  // so this one file serves both the Bridge app and the Nuxt 4 worktree.
  const disabledKeys = (o: IEntityDataItem) =>
    o.disabled ? { disabled: true, props: { disabled: true } } : {}

  const mapFn = doNotRemapToJustIdAndLabel
    ? (o: IEntityDataItem): ISelectItem => ({
        // Any label `o` already has is overridden.
        ...withLabelFromProperty(o, labelPropertyName),
        ...disabledKeys(o),
      })
    : (o: IEntityDataItem): ISelectItem =>
        o.divider
          ? // V3 keys a non-item row off `item.type`; Vuetify 2's bare `divider: true`
            // arrives as an ordinary item instead, which is why this rendered a
            // selectable row literally titled "Divider". Same fix already landed in
            // services/products.ts, suppliers.ts and supplierProducts.ts. `label` is kept
            // so the item still satisfies ISelectItem; VSelect renders a VDivider and
            // never reads it.
            { ...o, label: 'Divider', type: 'divider' }
          : {
              id: o.id,
              label: get(o, labelPropertyName),
              // Only when true, so selectable items stay exactly { id, label }:
              // that object is what a doReturnObject field writes into the
              // request body.
              ...disabledKeys(o),
            }

  // set up sort

  const sortFn =
    sortFunction ||
    ((a: ISelectItem, b: ISelectItem) =>
      a.label > b.label ? 1 : a.label === b.label ? 0 : -1)

  // make sure we have an array

  if (!Array.isArray(listData)) {
    listData = [listData]
  }

  // apply filter (async-safe: awaits each predicate result)

  const filtered: IEntityDataItem[] = []
  for (const item of listData) {
    if (await filterFn(item)) {
      filtered.push(item)
    }
  }

  const selectList = filtered
    .map((o) => ({
      ...o,
      disabled: disableFn(o),
    }))
    .map(mapFn)
    .sort(sortFn)

  // Decorate after the sort, so the suffix can't change where an item lands.
  return disabledItemLabelSuffix
    ? selectList.map((item) =>
        item.disabled
          ? { ...item, label: `${item.label}${disabledItemLabelSuffix}` }
          : item
      )
    : selectList
}

//

interface ISetSelectListFromListData
  extends Omit<IGetSelectList, 'calledFromFn' | 'getService' | 'getServiceId'> {
  fn: string
  listData: IEntityDataItem[]
}

async function _setSelectListFromListData({
  fn,
  listData,
  inputFields,
  targetFieldName,
  filterFunction,
  filterFieldName,
  disableFunction,
  disableFieldName,
  sortFunction,
  labelPropertyName,
  doNotRemapToJustIdAndLabel,
  disabledItemLabelSuffix,
}: ISetSelectListFromListData) {
  const selectList = await _getSelectListFromListData({
    listData,
    inputFields,
    labelPropertyName,
    filterFunction,
    filterFieldName,
    disableFunction,
    disableFieldName,
    sortFunction,
    doNotRemapToJustIdAndLabel,
    disabledItemLabelSuffix,
  })
  _setTargetFieldItemsFromSelectList({
    fn,
    inputFields,
    targetFieldName,
    selectList,
  })
}

function _setTargetFieldItemsFromSelectList({
  fn,
  inputFields,
  targetFieldName,
  selectList,
}: {
  fn: string
  inputFields: IEntityMaskData[]
  targetFieldName: string | undefined
  selectList: ISelectItem[]
}) {
  const targetField: IEntityMaskData | undefined = inputFields.find(
    (ifield) => ifield.name == targetFieldName
  )
  if (!targetField) {
    return
  }
  targetField.items = selectList
  console.debug(`${fn} targetField.items set to`, targetField.items)
}

function _clearTargetFieldItems({
  fn,
  inputFields,
  targetFieldName,
}: {
  fn: string
  inputFields: IEntityMaskData[]
  targetFieldName: string | undefined
}) {
  const targetField: IEntityMaskData | undefined = inputFields.find(
    (ifield) => ifield.name == targetFieldName
  )
  if (!targetField) {
    return
  }
  targetField.items = []
  console.debug(`${fn} targetField.items cleared. Set to`, targetField.items)
}

function _showLoading({
  inputFields,
  targetFieldName,
  doShow = true,
}: {
  inputFields: IEntityMaskData[]
  targetFieldName: string | undefined
  doShow?: boolean
}) {
  const targetField: IEntityMaskData | undefined = inputFields.find(
    (ifield) => ifield.name == targetFieldName
  )
  if (!targetField) {
    return
  }
  targetField.noDataText = doShow ? 'Loading...' : 'No data available'
}

//
// Exported functions
//

export async function setItemsForSelect({
  calledFromFn,
  inputFields,
  targetFieldName,
  getService,
  getServiceId,
  getServiceQueryParameter,
  filterFunction,
  filterFieldName,
  disableFunction,
  disableFieldName,
  sortFunction,
  labelPropertyName,
  doNotRemapToJustIdAndLabel,
  disabledItemLabelSuffix,
}: IGetSelectList) {
  const fn =
    `${calledFromFn} ---> [services/entityInputFieldsService]` +
    ` getSelectList (${simplePluralize(
      targetFieldName ?? 'No targetFieldName'
    )}) |`

  try {
    const listDta = getServiceId
      ? await getService(getServiceId, getServiceQueryParameter)
      : await getService()

    const listData = Array.isArray(listDta) ? listDta : [listDta]

    console.debug(`${fn} listData:`, listData)
    console.debug(`${fn} labelPropertyName:`, labelPropertyName)
    await _setSelectListFromListData({
      fn,
      listData,
      inputFields,
      targetFieldName,
      filterFunction,
      filterFieldName,
      disableFunction,
      disableFieldName,
      sortFunction,
      labelPropertyName,
      doNotRemapToJustIdAndLabel,
      disabledItemLabelSuffix,
    })
  } catch (e) {
    console.error(`${fn} CAUGHT ERROR IN SET ITEMS FOR SELECT`, e)
    if (isAuthFailure(e)) {
      logoutUser()
    }
    throw e
  }
}

export async function setItemsForSelectBasedOnFieldValueId({
  calledFromFn,
  inputFields,
  basedOnFieldName,
  targetFieldName,
  getService,
  getServiceQueryParameter,
  filterFunction,
  filterFieldName,
  disableFunction,
  disableFieldName,
  sortFunction,
  doNotRemapToJustIdAndLabel,
  disabledItemLabelSuffix,
  labelPropertyName,
}: IGetSelectListBasedOnField) {
  const fn =
    `${calledFromFn} ---> [services/entityInputFieldsService]` +
    ` getSelectList (${basedOnFieldName}'s ${simplePluralize(
      targetFieldName ?? 'No targetFieldName'
    )}) |`

  const basedOnField: IEntityMaskData | undefined = inputFields.find((ifield) =>
    isEqual(ifield.name, basedOnFieldName)
  )
  if (!basedOnField) {
    console.error(`${fn} 'basedOnField' is falsey. Should never happen.`)
    return
  }
  if (!basedOnField.value) {
    console.debug(
      `${fn} 'basedOnField.value' is falsey. Hasn't been set yet.` +
        ` Deferring setting items for select list.`
    )
    return
  }
  _clearTargetFieldItems({
    fn,
    inputFields,
    targetFieldName,
  })
  _showLoading({
    inputFields,
    targetFieldName,
  })
  const basedOnObject = basedOnField.value as ISelectItem
  try {
    const listData = getServiceQueryParameter
      ? await getService(basedOnObject.id, getServiceQueryParameter)
      : await getService(basedOnObject.id)
    console.debug(`${fn} listData:`, listData)
    _showLoading({
      inputFields,
      targetFieldName,
      doShow: false,
    })
    await _setSelectListFromListData({
      fn,
      listData,
      inputFields,
      targetFieldName,
      filterFunction,
      filterFieldName,
      disableFunction,
      disableFieldName,
      sortFunction,
      labelPropertyName,
      doNotRemapToJustIdAndLabel,
      disabledItemLabelSuffix,
    })
  } catch (e) {
    console.error(
      `${fn} CAUGHT ERROR IN SET ITEMS FOR SELECT BASED ON FIELD VALUE ID`,
      e
    )
    if (isAuthFailure(e)) {
      logoutUser()
    }
    throw e
  }
}

export async function setItemsForSelectBasedOnFieldValueIdsArrayAndSecondFieldValueId({
  calledFromFn,
  inputFields,
  basedOnFieldName,
  basedOnField2Name,
  targetFieldName,
  getService,
  filterFunction,
  filterFieldName,
  disableFunction,
  disableFieldName,
  sortFunction,
  doNotRemapToJustIdAndLabel,
  disabledItemLabelSuffix,
  labelPropertyName,
}: IGetSelectListBasedOnTwoFields) {
  const fn =
    `${calledFromFn} ---> [services/entityInputFieldsService]` +
    ` getSelectList (${basedOnFieldName}'s ${simplePluralize(
      targetFieldName ?? 'No targetFieldName'
    )}) |`

  const basedOnField: IEntityMaskData | undefined = inputFields.find(
    (ifield) => ifield.name == basedOnFieldName
  )
  if (!basedOnField?.value) {
    console.error(
      `${fn} basedOnField '${basedOnFieldName}' is falsey. Should never happen.`
    )
    return
  }

  const basedOnField2: IEntityMaskData | undefined = inputFields.find(
    (ifield) => ifield.name == basedOnField2Name
  )
  if (!basedOnField2?.value) {
    console.error(
      `${fn} basedOnField2 '${basedOnField2Name}' is falsey. Should never happen.`
    )
    return
  }

  const basedOnObjectArray = basedOnField.value as ISelectItem[]
  const basedOnIdArray = basedOnObjectArray.map((obj) => obj.id)
  const basedOnObject2 = basedOnField2.value as ISelectItem

  try {
    const listData = await getService(basedOnIdArray, basedOnObject2.id)
    console.debug(`${fn} listData:`, listData)

    await _setSelectListFromListData({
      fn,
      listData,
      inputFields,
      targetFieldName,
      filterFunction,
      filterFieldName,
      disableFunction,
      disableFieldName,
      sortFunction,
      labelPropertyName,
      doNotRemapToJustIdAndLabel,
      disabledItemLabelSuffix,
    })
  } catch (e) {
    console.debug(
      `${fn} CAUGHT ERROR IN setItemsForSelectBasedOnFieldValueIdsArrayAndSecondFieldValueId`,
      e
    )
    if (isAuthFailure(e)) {
      logoutUser()
    }
    throw e
  }
}

export async function setItemsForSelectBasedOnParentIdAndFieldValueId({
  calledFromFn,
  inputFields,
  parentId,
  basedOnFieldName,
  targetFieldName,
  getService,
  filterFunction,
  filterFieldName,
  disableFunction,
  disableFieldName,
  sortFunction,
  doNotRemapToJustIdAndLabel,
  disabledItemLabelSuffix,
  labelPropertyName,
}: IGetSelectListBasedOnParentIdAndFieldValueId) {
  const fn =
    `${calledFromFn} ---> [services/entityInputFieldsService]` +
    ` getSelectList (${basedOnFieldName}'s ${simplePluralize(
      targetFieldName ?? 'No targetFieldName'
    )}) |`

  if (!parentId) {
    console.error(
      `${fn} parentId '${parentId}' is falsey. Should never happen.`
    )
    return
  }

  const basedOnField: IEntityMaskData | undefined = inputFields.find(
    (ifield) => ifield.name == basedOnFieldName
  )
  if (!basedOnField?.value) {
    console.error(
      `${fn} basedOnField '${basedOnFieldName}' is falsey. Should never happen.`
    )
    return
  }

  const basedOnObject = basedOnField.value as ISelectItem

  try {
    const listData = await getService(parentId, basedOnObject.id)
    console.debug(`${fn} listData:`, listData)

    await _setSelectListFromListData({
      fn,
      listData,
      inputFields,
      targetFieldName,
      filterFunction,
      filterFieldName,
      disableFunction,
      disableFieldName,
      sortFunction,
      labelPropertyName,
      doNotRemapToJustIdAndLabel,
      disabledItemLabelSuffix,
    })
  } catch (e) {
    console.debug(
      `${fn} CAUGHT ERROR IN setItemsForSelectBasedOnFieldValueIdsArrayAndSecondFieldValueId`,
      e
    )
    if (isAuthFailure(e)) {
      logoutUser()
    }
    throw e
  }
}

export async function setItemsForSelectBasedOnParentIdAndTwoFieldValueIds({
  calledFromFn,
  inputFields,
  parentId,
  basedOnFieldName,
  basedOnField2Name,
  targetFieldName,
  noErrorOnFalseyBasedOnFieldName = false,
  getService,
  filterFunction,
  filterFieldName,
  disableFunction,
  disableFieldName,
  sortFunction,
  doNotRemapToJustIdAndLabel,
  disabledItemLabelSuffix,
  labelPropertyName,
}: IGetSelectListBasedOnParentIdAndTwoFieldValueIds) {
  const fn =
    `${calledFromFn} ---> [services/entityInputFieldsService]` +
    ` getSelectList (${basedOnFieldName}'s ${simplePluralize(
      targetFieldName ?? 'No targetFieldName'
    )}) |`

  if (!parentId) {
    console.error(
      `${fn} parentId '${parentId}' is falsey. Should never happen.`
    )
    return
  }

  const basedOnField: IEntityMaskData | undefined = inputFields.find(
    (ifield) => ifield.name == basedOnFieldName
  )
  if (!basedOnField?.value) {
    if (noErrorOnFalseyBasedOnFieldName) {
      console.debug(`${fn} basedOnField '${basedOnFieldName}' is falsey.`)
    } else {
      console.error(
        `${fn} basedOnField '${basedOnFieldName}' is falsey. Should never happen.`
      )
    }
    return
  }

  const basedOnField2: IEntityMaskData | undefined = inputFields.find(
    (ifield) => ifield.name == basedOnField2Name
  )
  if (!basedOnField2?.value) {
    console.error(
      `${fn} basedOnField '${basedOnField2Name}' is falsey. Should never happen.`
    )
    return
  }

  const basedOnObject = basedOnField.value as ISelectItem
  const basedOnObject2 = basedOnField2.value as ISelectItem

  console.debug(
    `${fn} parentId:`,
    parentId,
    'basedOnObject.id:',
    basedOnObject.id,
    'basedOnObject2.id:',
    basedOnObject2.id
  )

  try {
    const listData = await getService(
      parentId,
      basedOnObject.id,
      basedOnObject2.id
    )
    console.debug(`${fn} listData:`, listData)

    await _setSelectListFromListData({
      fn,
      listData,
      inputFields,
      targetFieldName,
      filterFunction,
      filterFieldName,
      disableFunction,
      disableFieldName,
      sortFunction,
      labelPropertyName,
      doNotRemapToJustIdAndLabel,
      disabledItemLabelSuffix,
    })
  } catch (e) {
    console.debug(
      `${fn} CAUGHT ERROR IN setItemsForSelectBasedOnFieldValueIdsArrayAndSecondFieldValueId`,
      e
    )
    if (isAuthFailure(e)) {
      logoutUser()
    }
    throw e
  }
}

export async function setItemsForSelectBasedOnParentIdAFieldValueIdAndAnAssociationId({
  calledFromFn,
  inputFields,
  parentId,
  associationId,
  basedOnFieldName,
  targetFieldName,
  getService,
  filterFunction,
  filterFieldName,
  disableFunction,
  disableFieldName,
  sortFunction,
  doNotRemapToJustIdAndLabel,
  disabledItemLabelSuffix,
  labelPropertyName,
}: IGetSelectListBasedOnParentIdAnAssociationIdAndAFieldValueId) {
  const fn =
    `${calledFromFn} ---> [services/entityInputFieldsService]` +
    ` getSelectList (${basedOnFieldName}'s ${simplePluralize(
      targetFieldName ?? 'No targetFieldName'
    )}) |`

  if (!parentId) {
    console.error(
      `${fn} parentId '${parentId}' is falsey. Should never happen.`
    )
    return
  }

  const basedOnField: IEntityMaskData | undefined = inputFields.find(
    (ifield: IEntityMaskData) => ifield.name == basedOnFieldName
  )
  if (!basedOnField?.value) {
    console.error(
      `${fn} basedOnField '${basedOnFieldName}' is falsey. Should never happen.`
    )
    return
  }

  if (!associationId) {
    console.error(
      `${fn} associationId '${associationId}' is falsey. Should never happen.`
    )
    return
  }

  const basedOnObject = basedOnField.value as ISelectItem

  try {
    const listData = await getService(parentId, basedOnObject.id, associationId)
    console.debug(`${fn} listData:`, listData)

    await _setSelectListFromListData({
      fn,
      listData,
      inputFields,
      targetFieldName,
      filterFunction,
      filterFieldName,
      disableFunction,
      disableFieldName,
      sortFunction,
      labelPropertyName,
      doNotRemapToJustIdAndLabel,
      disabledItemLabelSuffix,
    })
  } catch (e) {
    console.debug(
      `${fn} CAUGHT ERROR IN setItemsForSelectBasedOnFieldValueIdsArrayAndSecondFieldValueId`,
      e
    )
    if (isAuthFailure(e)) {
      logoutUser()
    }
    throw e
  }
}

export function setItemsForSelectEqualToFieldValue({
  calledFromFn,
  inputFields,
  sourceFieldName,
  targetFieldName,
}: {
  calledFromFn: string
  inputFields: IEntityMaskData[]
  sourceFieldName: string
  targetFieldName: string
}) {
  const sourceField = inputFields.find(
    (ifield) => ifield.name === sourceFieldName
  )
  if (!sourceField?.value || sourceField.value.constructor !== Array) {
    return
  }
  const targetField = inputFields.find(
    (ifield) => ifield.name === targetFieldName
  )
  if (!targetField) {
    return
  }
  targetField.items = cloneDeep(sourceField.value) // clone to avoid aliasing
}

export async function getItemsForSelectBasedOnFieldValueId({
  calledFromFn,
  inputFields,
  basedOnFieldName,
  targetFieldName,
  getService,
  filterFunction,
  filterFieldName,
  disableFunction,
  disableFieldName,
  sortFunction,
  doNotRemapToJustIdAndLabel,
  disabledItemLabelSuffix,
  labelPropertyName,
}: IGetSelectListBasedOnField) {
  const fn =
    `${calledFromFn} ---> [services/entityInputFieldsService]` +
    ` getSelectList (${basedOnFieldName}'s ${simplePluralize(
      targetFieldName ?? 'No targetFieldName'
    )}) |`

  const basedOnField: IEntityMaskData | undefined = inputFields.find(
    (ifield) => ifield.name == basedOnFieldName
  )
  if (!basedOnField?.value) {
    console.error(`${fn} 'basedOnField' is falsey. Should never happen.`)
    return
  }

  const basedOnObject = basedOnField.value as ISelectItem
  try {
    const listData = await getService(basedOnObject.id)
    console.debug(`${fn} listData:`, listData)

    return await _getSelectListFromListData({
      listData,
      inputFields,
      filterFunction,
      filterFieldName,
      disableFunction,
      disableFieldName,
      sortFunction,
      labelPropertyName,
      doNotRemapToJustIdAndLabel,
      disabledItemLabelSuffix,
    })
  } catch (e) {
    console.debug(
      `${fn} CAUGHT ERROR IN setItemsForSelectBasedOnFieldValueIdsArrayAndSecondFieldValueId`,
      e
    )
    if (isAuthFailure(e)) {
      logoutUser()
    }
    throw e
  }
}

export const noSort = (a: IEntityDataItem, b: IEntityDataItem) =>
  0 as TSortFunctionResult
